import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Save, Upload, Trash2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

type Brand = {
  name: string;
  tagline: string;
  supportEmail: string;
  logoUrl: string;
};

type Ads = {
  enabled: boolean;
  message: string;
  ctaLabel: string;
  ctaUrl: string;
};

export default function AdminBrand() {
  const [brand, setBrand] = useState<Brand>({ name: "", tagline: "", supportEmail: "", logoUrl: "" });
  const [ads, setAds] = useState<Ads>({ enabled: false, message: "", ctaLabel: "", ctaUrl: "" });
  const [loading, setLoading] = useState(true);
  const [savingBrand, setSavingBrand] = useState(false);
  const [savingAds, setSavingAds] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("app_settings")
        .select("key,value")
        .in("key", ["brand", "ads"]);
      if (data) {
        for (const row of data) {
          const v = (row.value as any) || {};
          if (row.key === "brand") {
            setBrand({
              name: v.name ?? "",
              tagline: v.tagline ?? "",
              supportEmail: v.supportEmail ?? "",
              logoUrl: v.logoUrl ?? "",
            });
          } else if (row.key === "ads") {
            setAds({
              enabled: !!v.enabled,
              message: v.message ?? "",
              ctaLabel: v.ctaLabel ?? "",
              ctaUrl: v.ctaUrl ?? "",
            });
          }
        }
      }
      setLoading(false);
    })();
  }, []);

  const saveBrand = async () => {
    setSavingBrand(true);
    const { error } = await supabase
      .from("app_settings")
      .upsert({ key: "brand", value: brand as any }, { onConflict: "key" });
    setSavingBrand(false);
    if (error) return toast.error("Failed to save", { description: error.message });
    toast.success("Brand settings saved");
  };

  const saveAds = async () => {
    setSavingAds(true);
    const { error } = await supabase
      .from("app_settings")
      .upsert({ key: "ads", value: ads as any }, { onConflict: "key" });
    setSavingAds(false);
    if (error) return toast.error("Failed to save", { description: error.message });
    toast.success("Ads settings saved");
  };

  const handleLogoUpload = async (file: File) => {
    setUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `logo-${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from("brand-assets")
        .upload(path, file, { upsert: true, cacheControl: "3600" });
      if (error) throw error;
      const { data } = supabase.storage.from("brand-assets").getPublicUrl(path);
      const next = { ...brand, logoUrl: data.publicUrl };
      setBrand(next);
      const { error: saveErr } = await supabase
        .from("app_settings")
        .upsert({ key: "brand", value: next as any }, { onConflict: "key" });
      if (saveErr) throw saveErr;
      toast.success("Logo uploaded");
    } catch (e: any) {
      toast.error("Upload failed", { description: e.message });
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    const next = { ...brand, logoUrl: "" };
    setBrand(next);
    await supabase
      .from("app_settings")
      .upsert({ key: "brand", value: next as any }, { onConflict: "key" });
    toast.success("Logo removed");
  };

  if (loading) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Brand</CardTitle>
          <CardDescription>
            Logo, store name, tagline and support contact shown across the platform.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 max-w-xl">
          <div className="space-y-2">
            <Label>Logo</Label>
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-lg border bg-muted flex items-center justify-center overflow-hidden">
                {brand.logoUrl ? (
                  <img src={brand.logoUrl} alt="Logo" className="h-full w-full object-contain" />
                ) : (
                  <ImageIcon className="h-8 w-8 text-muted-foreground" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleLogoUpload(f);
                    e.target.value = "";
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {brand.logoUrl ? "Replace logo" : "Upload logo"}
                </Button>
                {brand.logoUrl && (
                  <Button type="button" variant="ghost" size="sm" onClick={removeLogo}>
                    <Trash2 className="h-4 w-4 mr-2" /> Remove
                  </Button>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              PNG or SVG recommended, square aspect, transparent background.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Store name</Label>
            <Input id="name" value={brand.name} onChange={(e) => setBrand({ ...brand, name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tagline">Tagline</Label>
            <Input id="tagline" value={brand.tagline} onChange={(e) => setBrand({ ...brand, tagline: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Support email</Label>
            <Input
              id="email"
              type="email"
              value={brand.supportEmail}
              onChange={(e) => setBrand({ ...brand, supportEmail: e.target.value })}
            />
          </div>

          <Button onClick={saveBrand} disabled={savingBrand}>
            {savingBrand ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save brand
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Announcement / Ad banner</CardTitle>
          <CardDescription>
            Show a promotional message or announcement at the top of the customer app.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 max-w-xl">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <div className="font-medium text-sm">Show banner</div>
              <div className="text-xs text-muted-foreground">
                When enabled, the message below appears for all customers.
              </div>
            </div>
            <Switch
              checked={ads.enabled}
              onCheckedChange={(v) => setAds({ ...ads, enabled: v })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="msg">Message</Label>
            <Textarea
              id="msg"
              rows={3}
              placeholder="🎉 Black Friday - 50% off Pro for the next 48 hours!"
              value={ads.message}
              onChange={(e) => setAds({ ...ads, message: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="cta">Button label</Label>
              <Input
                id="cta"
                placeholder="Upgrade now"
                value={ads.ctaLabel}
                onChange={(e) => setAds({ ...ads, ctaLabel: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ctaUrl">Button URL</Label>
              <Input
                id="ctaUrl"
                placeholder="/settings"
                value={ads.ctaUrl}
                onChange={(e) => setAds({ ...ads, ctaUrl: e.target.value })}
              />
            </div>
          </div>

          <Button onClick={saveAds} disabled={savingAds}>
            {savingAds ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save ads
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
